import { fromJS } from 'immutable';
import ACTIONS from '../app.constants';
import { CitationReducerActionType, CitationReducer, CitationList } from '../modules/citation/types';
import { CommentList, Comment } from '../modules/application/types';
import { File } from '../modules/application/types';

export default function citationReducer(state = fromJS({}), action: CitationReducerActionType): CitationReducer {
    switch (action.type) {
        case ACTIONS.GET_CITATION_LIST.PENDING: {
            return state.setIn(['citationList', 'loading'], true);
        }
        case ACTIONS.GET_CITATION_LIST.SUCCESS: {
            let loadedList = state.getIn(['citationList', 'data']) ?? [];
            const citationList: CitationList = action.data as CitationList;
            const loadMore = citationList.length < action?.paginationData?.limit ? false : true;

            if (loadedList && action.paginationData.skip > 0) {
                loadedList = loadedList.concat(citationList);
            } else {
                loadedList = citationList;
            }

            return state
                .setIn(['citationList', 'data'], loadedList)
                .setIn(['citationList', 'loadMore'], loadMore)
                .setIn(['citationList', 'loading'], false);
        }
        case ACTIONS.GET_CITATION_LIST.ERROR: {
            return state.setIn(['citationList', 'loading'], false);
        }
        case ACTIONS.CHANGE_CITATION_FILTERS: {
            return state.setIn(['citationFilters', 'filterSelections'], action.payload.data);
        }

        case ACTIONS.SET_CITATION_LIST_COUNT.PENDING: {
            return state.setIn(['citationList', 'loading'], false).setIn(['citationList'], []);
        }
        case ACTIONS.SET_CITATION_LIST_COUNT.SUCCESS: {
            return state.setIn(['citationList', 'citationCount'], action.citationCount);
        }
        case ACTIONS.SET_CITATION_LIST_COUNT.ERROR: {
            return state.setIn(['citationList', 'loading'], false).setIn(['citationList'], []);
        }
        case ACTIONS.GET_CITATION_DETAIL.PENDING: {
            return state.setIn(['citationDetail', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_CITATION_DETAIL.SUCCESS: {
            return state
                .setIn(['citationDetail', 'loading'], false)
                .setIn(['citationDetail', 'data'], action.data.citation)
                .setIn(['citationEditForm', 'editForm'], action.data.editForm)
                .setIn(['citationType', 'data'], action.data.citationType);
        }

        case ACTIONS.GET_CITATION_DETAIL.ERROR: {
            return state.setIn(['citationDetail', 'loading'], false);
        }

        case ACTIONS.UPDATE_CITATION.PENDING: {
            return state.setIn(['citationData', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.UPDATE_CITATION.SUCCESS: {
            return state.setIn(['citationData', 'loading'], false).setIn(['citationDetail', 'data'], action.data);
        }
        case ACTIONS.UPDATE_CITATION.ERROR: {
            return state.setIn(['citationDetail', 'loading'], false).setIn(['data', 'error'], action.error);
        }
        case ACTIONS.ADD_CITATION_COMMENT.PENDING: {
            return state.setIn(['citationComments', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_CITATION_COMMENT.SUCCESS: {
            const data: Comment = action.data;
            const comments: CommentList = state.getIn(['citationComments', 'data']) ?? [];
            comments.unshift(data);
            return state.setIn(['citationComments', 'loading'], false).setIn(['citationComments', 'data'], comments);
        }
        case ACTIONS.ADD_CITATION_COMMENT.ERROR: {
            return state
                .setIn(['citationComments', 'loading'], false)
                .setIn(['citationComments', 'error'], action.error);
        }
        case ACTIONS.CITATION_COMMENT_LIST.PENDING: {
            return state.setIn(['citationComments', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.CITATION_COMMENT_LIST.SUCCESS: {
            let loadedList = state.getIn(['citationComments', 'data']) ?? [];
            const citationCommentData: CommentList = action.data as CommentList;
            const loadMore = citationCommentData.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(citationCommentData);
            } else {
                loadedList = citationCommentData;
            }
            const citationCommentsList = state
                .setIn(['citationComments', 'loadMore'], loadMore)
                .setIn(['citationComments', 'data'], loadedList);
            return citationCommentsList;
        }
        case ACTIONS.CITATION_COMMENT_LIST.ERROR: {
            return state.setIn(['citationComments', 'loading'], false);
        }
        case ACTIONS.GET_CITATIONS_FILE_LIST.PENDING: {
            return state.setIn(['citationFiles', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_CITATIONS_FILE_LIST.SUCCESS: {
            let loadedList = state.getIn(['citationFiles', 'data']) ?? [];
            const FilesList = action.data;
            const loadMore = FilesList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(FilesList);
            } else {
                loadedList = FilesList;
            }
            return state
                .setIn(['citationFiles', 'loading'], false)
                .setIn(['citationFiles', 'loadMore'], loadMore)
                .setIn(['citationFiles', 'data'], loadedList);
        }
        case ACTIONS.GET_CITATIONS_FILE_LIST.ERROR: {
            return state.setIn(['citationFiles', 'loading'], false);
        }

        case ACTIONS.DELETE_CITATIONS_FILE_DOCUMENT.PENDING: {
            return state.setIn(['citationFiles', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DELETE_CITATIONS_FILE_DOCUMENT.SUCCESS: {
            const fileId = decodeURIComponent(action.payload.fileId);
            const files = state.getIn(['citationFiles', 'data']);
            const filteredFiles = files.filter((file: File) => file.id !== fileId);
            return state.setIn(['citationFiles', 'loading'], false).setIn(['citationFiles', 'data'], filteredFiles);
        }
        case ACTIONS.DELETE_CITATIONS_FILE_DOCUMENT.ERROR: {
            return state.setIn(['citationFiles', 'loading'], false).setIn(['citationFiles', 'error'], action.error);
        }

        case ACTIONS.DOWNLOAD_CITATIONS_FILE_DOCUMENT.PENDING: {
            return state.setIn(['citationFiles', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DOWNLOAD_CITATIONS_FILE_DOCUMENT.SUCCESS: {
            return state.setIn(['citationFiles', 'loading'], false);
        }
        case ACTIONS.DOWNLOAD_CITATIONS_FILE_DOCUMENT.ERROR: {
            return state.setIn(['citationFiles', 'loading'], false);
        }
        case ACTIONS.GET_CITATION_SEARCH_FORM.PENDING: {
            return state.setIn(['citationSearchForm', 'loading'], true);
        }

        case ACTIONS.GET_CITATION_SEARCH_FORM.SUCCESS: {
            return state
                .setIn(['citationSearchForm', 'loading'], false)
                .setIn(['citationSearchForm', 'data'], action.data);
        }

        case ACTIONS.GET_CITATION_SEARCH_FORM.ERROR: {
            return state.setIn(['citationSearchForm', 'loading'], false);
        }
    }

    return state;
}
